import React, { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { motion } from 'motion/react'

import { HgChatInspection, HgInspectedItem, HgInspectedTurn, inspectChat } from '../api/client'

// Admin only: the whole record of one Chats conversation (docs/chats.md §10).
// Every item, including the ones the page and the model never show; every
// turn with its routing. Technical on purpose, and kept terse.

const ICON_SPRING = { type: 'spring' as const, stiffness: 400, damping: 20 }

const apiMessage = (e: any, fallback: string): string =>
  e?.response?.data?.error || e?.response?.data?.detail || e?.message || fallback

const Json: React.FC<{ value: unknown }> = ({ value }) => (
  <pre className="text-xs whitespace-pre-wrap break-words rounded border border-gray-200 bg-gray-50 p-2 max-h-80 overflow-y-auto text-gray-700">
    {typeof value === 'string' ? value : JSON.stringify(value, null, 2)}
  </pre>
)

/** What a reasoning item says in words, if the provider gave any: OpenAI's
 * `summary` parts, or Anthropic's `thinking` text. */
export const reasoningSummary = (content: Record<string, any> | null): string | null => {
  const data = content?.data
  if (!data || typeof data !== 'object') return null
  if (Array.isArray(data.summary)) {
    const text = data.summary
      .map((part: any) => (typeof part?.text === 'string' ? part.text : ''))
      .filter(Boolean)
      .join('\n\n')
    if (text) return text
  }
  if (typeof data.thinking === 'string' && data.thinking.trim()) return data.thinking
  return null
}

export const ItemBody: React.FC<{ item: HgInspectedItem }> = ({ item }) => {
  const c = item.content ?? {}
  if (c.tombstone) {
    // Blanked by the retention sweep (docs/chats.md §1, "Retention").
    return (
      <div className="text-xs text-gray-400">
        Pruned{c.pruned_on ? ` ${c.pruned_on}` : ''}
        {typeof c.bytes === 'number' ? ` (was ${c.bytes} B)` : ''}
      </div>
    )
  }
  switch (item.item_type) {
    case 'compaction_summary':
      return (
        <div className="flex flex-col gap-1">
          <div className="font-mono text-xs text-gray-500">
            Summarizes through #{c.upto}; kept {c.kept_seqs?.length ?? 0} item(s) and the last{' '}
            {c.kept_exchanges} exchange(s); ~{c.tokens_before} → ~{c.tokens_after} tokens
            {Array.isArray(c.loaded_tools) && c.loaded_tools.length > 0 && (
              <> · loaded tools carried over: {c.loaded_tools.join(', ')}</>
            )}
          </div>
          <div className="text-sm text-gray-800 whitespace-pre-wrap">{c.text}</div>
        </div>
      )
    case 'user_message':
    case 'assistant_message':
    case 'persona_message':
      return (
        <div className="text-sm text-gray-800 whitespace-pre-wrap">
          {c.text || <span className="text-gray-400">(empty)</span>}
          {c.interrupted && <span className="ml-2 text-xs text-amber-700">interrupted</span>}
        </div>
      )
    case 'reasoning': {
      const summary = reasoningSummary(c)
      return summary ? (
        <div className="text-sm text-gray-600 italic whitespace-pre-wrap">{summary}</div>
      ) : (
        <div className="text-xs text-gray-400">No summary ({c.model ?? 'unknown model'}, encrypted)</div>
      )
    }
    case 'tool_call':
      return (
        <div className="flex flex-col gap-1">
          <div className="font-mono text-xs text-gray-800">{item.tool_name}</div>
          <Json value={c.arguments ?? {}} />
          {c.arguments_error && <Json value={`Unparsed: ${c.arguments_error}`} />}
        </div>
      )
    case 'tool_result':
      return (
        <div className={`flex flex-col gap-1 ${c.is_error ? 'text-rose-700' : ''}`}>
          {Array.isArray(c.loads) && (
            // A tool search's result: what it put on the menu from here on.
            <div className="font-mono text-xs text-gray-800">
              Loaded: {c.loads.length ? c.loads.join(', ') : 'nothing'}
            </div>
          )}
          <Json value={c.output} />
        </div>
      )
    case 'notice':
      return (
        <div className="flex flex-col gap-1">
          <div className="font-mono text-xs text-gray-800">
            {c.source}/{c.kind}
          </div>
          {c.data && Object.keys(c.data).length > 0 && <Json value={c.data} />}
        </div>
      )
    default:
      return <Json value={c} />
  }
}

const ItemRow: React.FC<{ item: HgInspectedItem }> = ({ item }) => {
  const [raw, setRaw] = useState(false)
  const flags = [item.in_ui ? 'ui' : null, item.in_model_context ? 'model' : null].filter(Boolean).join(' + ')
  return (
    <div className="rounded-md border border-gray-200 bg-white px-3 py-2 flex flex-col gap-1">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-500 font-mono">
        <span className="text-gray-800 font-semibold">#{item.seq}</span>
        <span>{item.item_type}</span>
        <span>{item.author_kind}</span>
        {item.turn_id != null && <span>turn {item.turn_id}</span>}
        {item.call_id && <span>call {item.call_id}</span>}
        {item.provider && <span>{item.provider}</span>}
        <span>{flags || 'hidden'}</span>
        <span>{item.content_bytes} B</span>
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          transition={ICON_SPRING}
          onClick={() => setRaw((v) => !v)}
          className="ml-auto text-gray-500 hover:text-gray-800"
        >
          {raw ? 'Hide raw' : 'Raw'}
        </motion.button>
      </div>
      {raw ? <Json value={item} /> : <ItemBody item={item} />}
    </div>
  )
}

const TURN_COLUMNS: [string, (t: HgInspectedTurn) => React.ReactNode][] = [
  ['Turn', (t) => t.id],
  ['Trigger', (t) => t.trigger_kind],
  ['Status', (t) => t.status],
  ['Role', (t) => t.role ?? '—'],
  ['Profile', (t) => t.profile ?? '—'],
  ['Model', (t) => (t.model ? `${t.provider}/${t.model}` : '—')],
  ['Effort', (t) => t.effort ?? '—'],
  ['Steps', (t) => t.steps],
  ['Tokens in / cached / out / reasoning', (t) =>
    `${t.prompt_tokens} / ${t.cached_tokens} / ${t.completion_tokens} / ${t.reasoning_tokens}`],
  ['Seqs', (t) => `${t.start_seq ?? '—'}–${t.end_seq ?? '—'}`],
  ['Error', (t) => t.error_code ?? '—'],
]

const ChatInspectorPage: React.FC = () => {
  const { conversationId } = useParams<{ conversationId?: string }>()
  const navigate = useNavigate()
  const [data, setData] = useState<HgChatInspection | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [jump, setJump] = useState(conversationId ?? '')

  useEffect(() => {
    setData(null)
    setError(null)
    const id = Number(conversationId)
    if (!conversationId || !Number.isInteger(id) || id <= 0) return
    let live = true
    inspectChat(id)
      .then((d) => live && setData(d))
      .catch((e) => live && setError(apiMessage(e, 'Could not load the chat')))
    return () => {
      live = false
    }
  }, [conversationId])

  const conversation = data?.conversation
  return (
    <div className="min-h-screen bg-blush-light">
      <div className="container mx-auto px-4 py-6 flex flex-col gap-4 max-w-5xl">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-bold text-gray-800">Chat inspector</h1>
          <form
            className="flex items-center gap-2 ml-auto"
            onSubmit={(e) => {
              e.preventDefault()
              if (jump.trim()) navigate(`/admin/chats/${jump.trim()}`)
            }}
          >
            <input
              value={jump}
              onChange={(e) => setJump(e.target.value)}
              placeholder="Chat id"
              inputMode="numeric"
              className="w-28 rounded-md border border-gray-300 px-2 py-1 text-sm"
            />
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              transition={ICON_SPRING}
              type="submit"
              className="px-3 py-1 rounded-md text-sm border border-gray-300 bg-white text-gray-700 hover:bg-gray-100"
            >
              Open
            </motion.button>
          </form>
        </div>

        {error && <div className="text-sm text-rose-700">{error}</div>}
        {!conversationId && <div className="text-sm text-gray-500">Open a chat by id.</div>}
        {conversationId && !data && !error && <div className="text-sm text-gray-500">Loading…</div>}

        {data && conversation && (
          <>
            <div className="rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 flex flex-wrap gap-x-6 gap-y-1">
              <span className="font-semibold text-gray-900">
                #{conversation.id} {conversation.title ?? '(untitled)'}
              </span>
              <span>
                user <Link className="underline" to={`/admin/users/${conversation.user_id}`}>{conversation.user_id}</Link>
              </span>
              <span>{conversation.status}</span>
              <span>profile override {conversation.profile_override ?? 'none'}</span>
              <span>{data.items.length} items</span>
            </div>

            <div className="overflow-x-auto rounded-md border border-gray-200 bg-white">
              <table className="min-w-full text-xs">
                <thead className="bg-gray-50 text-gray-500">
                  <tr>
                    {TURN_COLUMNS.map(([label]) => (
                      <th key={label} className="px-2 py-1 text-left font-medium whitespace-nowrap">
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.turns.map((t) => (
                    <tr key={t.id} className="border-t border-gray-100 font-mono text-gray-700">
                      {TURN_COLUMNS.map(([label, cell]) => (
                        <td key={label} className="px-2 py-1 whitespace-nowrap">
                          {cell(t)}
                        </td>
                      ))}
                    </tr>
                  ))}
                  {data.turns.length === 0 && (
                    <tr>
                      <td className="px-2 py-1 text-gray-400" colSpan={TURN_COLUMNS.length}>
                        No turns.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {data.proposals.length > 0 && (
              <div className="flex flex-col gap-1">
                <h2 className="text-sm font-semibold text-gray-700">Proposals</h2>
                <Json value={data.proposals} />
              </div>
            )}

            <div className="flex flex-col gap-2">
              {data.items.map((item) => (
                <ItemRow key={item.seq} item={item} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default ChatInspectorPage
